import { create } from '@bufbuild/protobuf'
import type { ConnectError } from '@connectrpc/connect'
import { useQuery } from '@connectrpc/connect-query'
import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { PlanSubmissionStatus } from '@ebalance/proto/bfftypes/v1/plan_submission_pb'
import type { ProcurementPlanRow } from '@ebalance/proto/bffuser/v1/procurement_plan_model_pb'
import { listProcurementPlans } from '@ebalance/proto/bffuser/v1/procurement_plan_service-ProcurementPlanService_connectquery'
import {
  ListProcurementPlansRequestSchema,
  type ListProcurementPlansResponse,
} from '@ebalance/proto/bffuser/v1/procurement_plan_service_pb'
import type { UseQueryResult } from '@tanstack/react-query'

import { toProtoDate } from '~/models/dateTime'

/** 一覧 polling の間隔 (ms)。bidding polling の既定 (5s) に整合。 */
const IN_FLIGHT_POLL_INTERVAL_MS = 5_000

/**
 * in-flight (非同期遷移中) の判定 — GENERATING (作成中) / SUBMITTING (処理中) /
 * APPROVAL_PENDING (承認待ち)。
 * この 2 状態のみが「結果がまだ確定していない」行で、 一覧に polling で追随
 * させる対象 (plansubmit-error-detail design 決定 #4)。
 */
function isInFlightStatus(status: PlanSubmissionStatus): boolean {
  return (
    status === PlanSubmissionStatus.GENERATING ||
    status === PlanSubmissionStatus.SUBMITTING ||
    status === PlanSubmissionStatus.APPROVAL_PENDING
  )
}

/**
 * 条件付き polling 判定 (純関数、抽出してテスト容易化): rows に in-flight 行が
 * 1 件でもあれば true。 rows 未取得 (初回ロード前) は false。
 */
export function hasInFlightRows(
  rows: ReadonlyArray<{ status: PlanSubmissionStatus }> | undefined,
): boolean {
  return rows !== undefined && rows.some((r) => isInFlightStatus(r.status))
}

/**
 * スポットの約定結果が未着の札がある行 (AC-PPP-019) があるか。件数が 0 になった
 * 応答で表示を消すため、0 件の応答が返るまで再取得を続ける (design Decisions #9)。
 */
export function hasAwaitingMatchResultRows(
  rows: ReadonlyArray<{ bidsAwaitingMatchResultCount: number }> | undefined,
): boolean {
  return (
    rows !== undefined && rows.some((r) => r.bidsAwaitingMatchResultCount > 0)
  )
}

type UseListProcurementPlansParams = {
  targetPeriodStartDate: Date
  /** 選択されたエリア (複数選択・OR 条件)。 空配列 = 全エリア (フィルタなし)。 */
  areaCodes: AreaCode[]
  /** true = 全バージョン取得、 false (default) = 最新のみ */
  includeAllVersions: boolean
  /**
   * false のとき query 自体を発火しない (procurement-plan-list-period-tab
   * 設計 Decision #4)。計画期間タブが「日次」以外のとき、一覧を表示しないため
   * 無駄な取得を避ける。
   */
  enabled: boolean
}

/**
 * ProcurementPlanService.ListProcurementPlans を呼ぶ hook。
 *
 * timeframe は送らない (= optional field 不在): 断面タブ撤去
 * (procurement-plan-list-three-section 設計) により、一覧は特定の断面に絞らず
 * 対象日の全断面 (翌日(当日)/翌日/翌々日) を横断取得する (AC-001/AC-003)。
 *
 * areaCodes は複数選択 OR 条件のエリアフィルタ (procurement-plan-list-area-
 * multi-select 設計 AC-01/AC-04)。 空配列 = 全エリア (フィルタなし)。
 *
 * global QueryClient の `throwOnError: true` を本 query だけ `false` に上書きする。
 * 取得失敗は AC-PLC-009 のとおり呼び出し元がテーブル位置の Alert バナーで扱うため、
 * ErrorLayout (全画面エラー) に飛ばさない。
 * 注意: テスト用 QueryClient (testUtils) は throwOnError: false を強制するため、
 * この上書きの欠落は F5 テストでは検出できない (プロダクトのみで全画面エラー化する)。
 */
export function useListProcurementPlans({
  targetPeriodStartDate,
  areaCodes,
  includeAllVersions,
  enabled,
}: UseListProcurementPlansParams): UseQueryResult<
  ListProcurementPlansResponse,
  ConnectError
> {
  return useQuery(
    listProcurementPlans,
    create(ListProcurementPlansRequestSchema, {
      targetPeriodStartDate: toProtoDate(targetPeriodStartDate),
      areaCodes,
      includeAllVersions,
    }),
    {
      enabled,
      throwOnError: false,
      // `useMarketProducts` の関数形式 refetchInterval 先例
      // (IntradayBidding/hooks/useMarketProducts.ts) に倣う: in-flight 行が
      // 無ければ polling off (`false`)。
      refetchInterval: (query) =>
        hasInFlightRows(query.state.data?.rows) ||
        hasAwaitingMatchResultRows(query.state.data?.rows)
          ? IN_FLIGHT_POLL_INTERVAL_MS
          : false,
    },
  )
}

export type { ProcurementPlanRow }
